import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format, addDays } from 'date-fns';

export const Hero = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);

  const handleSearch = () => {
    if (checkIn && checkOut) {
      navigate(`/rooms?checkIn=${checkIn.toISOString()}&checkOut=${checkOut.toISOString()}&guests=${guests}`);
    } else {
      navigate('/rooms');
    }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1722515177905-930b3e2bcea6?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Le Château d'Azur"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1F3C]/40 via-transparent to-[#0F1F3C]/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="mb-8"
        >
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-6">
            {t('hero.subtitle')}
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-white/80 font-sans text-lg max-w-xl mx-auto">
            {t('hero.tagline')}
          </p>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-40 lg:bottom-48"
        >
          <a
            href="#booking-widget"
            className="flex flex-col items-center text-white/60 hover:text-white transition-colors"
          >
            <span className="text-xs uppercase tracking-widest font-sans mb-2">{t('hero.discover')}</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </a>
        </motion.div>
      </div>

      {/* Booking Widget */}
      <motion.div
        id="booking-widget"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
        className="absolute bottom-0 left-0 right-0 z-20"
        data-testid="booking-widget"
      >
        <div className="max-w-5xl mx-auto px-6 pb-10">
          <div className="bg-white/95 backdrop-blur-md p-6 lg:p-8 shadow-[0_20px_40px_-15px_rgba(15,31,60,0.2)]">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
              {/* Check-in */}
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                  {t('booking.checkIn')}
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full flex items-center justify-between border-b border-gray-300 pb-2 text-[#0F1F3C] hover:border-[#0F1F3C] transition-colors"
                      data-testid="checkin-picker"
                    >
                      <span className="font-sans">
                        {checkIn ? format(checkIn, 'MMM dd, yyyy') : t('booking.selectDates')}
                      </span>
                      <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={checkIn}
                      onSelect={(date) => {
                        setCheckIn(date);
                        if (!checkOut || (date && checkOut <= date)) {
                          setCheckOut(addDays(date, 1));
                        }
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Check-out */}
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                  {t('booking.checkOut')}
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full flex items-center justify-between border-b border-gray-300 pb-2 text-[#0F1F3C] hover:border-[#0F1F3C] transition-colors"
                      data-testid="checkout-picker"
                    >
                      <span className="font-sans">
                        {checkOut ? format(checkOut, 'MMM dd, yyyy') : t('booking.selectDates')}
                      </span>
                      <Calendar className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={checkOut}
                      onSelect={setCheckOut}
                      disabled={(date) => date <= (checkIn || new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Guests */}
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                  {t('booking.guests')}
                </label>
                <div className="flex items-center justify-between border-b border-gray-300 pb-2">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-[#D4AF37]" />
                    <span className="font-sans text-[#0F1F3C]">
                      {guests} {guests === 1 ? t('booking.guest') : t('booking.guests')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      className="w-8 h-8 border border-gray-300 flex items-center justify-center hover:border-[#0F1F3C] transition-colors"
                      data-testid="guests-decrease"
                    >
                      -
                    </button>
                    <button
                      onClick={() => setGuests(Math.min(6, guests + 1))}
                      className="w-8 h-8 border border-gray-300 flex items-center justify-center hover:border-[#0F1F3C] transition-colors"
                      data-testid="guests-increase"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] px-8 py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
                data-testid="search-availability-btn"
              >
                {t('booking.searchAvailability')}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
